import { isProviderVisibleAtLocation } from "@/components/provider-select/provider-location-visibility";

import { Category } from "@/types/category";
import { ServiceVisibilityType } from "@/types/enums/service";
import { PublicLocationDetail } from "@/types/location";
import { PublicProviderSummary } from "@/types/provider";
import { PublicServiceDetail, PublicServiceSummary } from "@/types/service";

import {
  PROMOTION_CATEGORY_NAME,
  isPromotionCategoryName,
  isPromotionService,
  isSyntheticPromotionCategoryName,
} from "./promotion-category";
import { LocationType, PatientType } from "@/stores/self-schedule-store";

// Simpler interface for accordion/grouping use (no location/buckets complexity)
export interface SimpleServiceGroup {
  id: string;
  title: string;
  services: PublicServiceDetail[];
  /** 0 for top-level categories, >0 for subcategories. Drives rendering indentation. */
  depth?: number;
  /** Name of the parent category (undefined for roots). */
  parentCategoryName?: string | null;
}

export function computeAllowedLocationIds(
  locations: PublicLocationDetail[] | undefined,
  locationType: LocationType | null,
): Set<string> | null {
  if (!locations || locationType == null) return null;
  return new Set(
    locations
      .filter((loc) =>
        locationType === LocationType.ALL
          ? true
          : locationType === LocationType.VIRTUAL
            ? loc.isVirtual
            : !loc.isVirtual,
      )
      .map((l) => l.id),
  );
}

/**
 * Whether `service` can be booked as `locationType`. Telehealth is a virtual
 * service, which is by video wherever it's booked, or a service at a virtual
 * location (one with no links counts only when the clinic has a virtual
 * location). In person is any other service at a physical location.
 */
export function offersLocationType(
  service: PublicServiceDetail,
  locations: PublicLocationDetail[] | undefined,
  locationType: LocationType | null,
): boolean {
  if (!locations || locationType == null || locationType === LocationType.ALL)
    return true;
  const linked = locations.filter((location) =>
    service.locationIds?.includes(location.id),
  );
  if (locationType === LocationType.VIRTUAL) {
    if (service.isVirtual) return true;
    return service.locationIds?.length
      ? linked.some((location) => location.isVirtual)
      : locations.some((location) => location.isVirtual);
  }
  if (service.isVirtual) return false;
  return (
    !service.locationIds?.length ||
    linked.some((location) => !location.isVirtual)
  );
}

/**
 * Whether `location` can host every service in `services`. A service with no
 * explicit `locationIds` isn't pinned to a location, so it doesn't narrow the
 * choice (same rule as `filterServices`).
 */
export function canLocationHostServices(
  location: { id: string },
  services: PublicServiceDetail[],
): boolean {
  return services.every(
    (service) =>
      service.locationIds.length === 0 ||
      service.locationIds.includes(location.id),
  );
}

export interface FilterParams {
  searchQuery: string;
  selectedCategory: string | undefined;
  locations: PublicLocationDetail[] | undefined;
  selectedLocationId: string | null;
  selectedProviderId?: string | null;
  patientType: PatientType | null;
  locationType: LocationType | null;
  categories?: Category[];
}

export function filterServices(
  services: PublicServiceDetail[] | undefined,
  params: FilterParams,
): PublicServiceDetail[] {
  const {
    searchQuery,
    selectedCategory,
    locations,
    selectedLocationId,
    selectedProviderId,
    patientType,
    locationType,
    categories,
  } = params;
  if (!services) return [];
  const q = normalizeServiceSearchText(searchQuery).split(" ");
  return services.filter((s) => {
    if (s.visibility === ServiceVisibilityType.NO_PATIENTS) {
      return false;
    }
    // Empty s.locationIds means the service isn't explicitly assigned to
    // any location. Treat that as "available everywhere" — matches how the
    // RWG feed emits (service, location) pairs for unassigned services and
    // fixes the bug where single-location tenants with no
    // LocationToService rows showed an empty catalog because every service
    // failed the includes() check.
    const hasExplicitLocations = !!s.locationIds?.length;
    if (!offersLocationType(s, locations, locationType)) {
      return false;
    }
    if (
      selectedLocationId &&
      hasExplicitLocations &&
      !s.locationIds?.includes(selectedLocationId)
    ) {
      return false;
    }
    if (selectedProviderId) {
      const providerIds = s.effectiveProviderIds ?? s.providerIds ?? [];
      if (providerIds.length > 0 && !providerIds.includes(selectedProviderId)) {
        return false;
      }
    }
    if (selectedCategory && selectedCategory !== "all") {
      if (isSyntheticPromotionCategoryName(selectedCategory, categories)) {
        if (!isPromotionService(s)) return false;
      } else if (s.category !== selectedCategory) {
        return false;
      }
    }
    if (q.length === 0) {
      return true;
    }
    const matches = q.every((term) => {
      const titleMatch = s.name.toLowerCase().includes(term);
      const descMatch = s.description?.toLowerCase().includes(term) ?? false;
      const categoryMatch = s.category?.toLowerCase().includes(term) ?? false;
      const categoryDescMatch =
        s.category && categories
          ? (categories
              .find((cat) => cat.name === s.category)
              ?.description?.toLowerCase()
              .includes(term) ?? false)
          : false;
      const tagMatch =
        s.tags?.some((tag) => tag.name.toLowerCase().includes(term)) ?? false;
      if (
        !titleMatch &&
        !descMatch &&
        !categoryMatch &&
        !categoryDescMatch &&
        !tagMatch
      ) {
        return false;
      }
      return true;
    });
    if (!matches) {
      return false;
    }
    if (
      patientType !== PatientType.ALL &&
      patientType !== PatientType.EXISTING &&
      s.visibility === ServiceVisibilityType.EXISTING
    ) {
      return false;
    }
    if (
      patientType === PatientType.EXISTING &&
      s.visibility === ServiceVisibilityType.NEW
    ) {
      return false;
    }
    return true;
  });
}

export function getServicePricingForSelfSchedule(
  service: PublicServiceSummary,
) {
  const hasPromotion = !!service.promotionId || !!service.promotionName;
  const checkoutPrice = service.price;

  if (!hasPromotion || checkoutPrice == null || checkoutPrice <= 0) {
    return {
      price: checkoutPrice,
      displayPrice: service.displayPrice ?? 0,
      displayPriceMax: service.displayPriceMax ?? 0,
      priceStartsAt: service.priceStartsAt,
      estimated: service.estimated,
      originalDisplayPrice: service.originalDisplayPrice,
      originalDisplayPriceMax: service.originalDisplayPriceMax,
      promotionName: service.promotionName,
    };
  }

  const percentage = service.promotionDiscountPercentage;
  const discountAmount =
    percentage != null && percentage > 0
      ? Math.floor((checkoutPrice * percentage) / 100)
      : (service.promotionDiscountAmount ?? 0);
  const promotedCheckoutPrice = Math.max(0, checkoutPrice - discountAmount);

  return {
    price: checkoutPrice,
    displayPrice: promotedCheckoutPrice,
    displayPriceMax: undefined,
    priceStartsAt: false,
    estimated: false,
    originalDisplayPrice: checkoutPrice,
    originalDisplayPriceMax: undefined,
    promotionName: service.promotionName,
  };
}

export function sortServicesAlpha(
  services: PublicServiceDetail[],
): PublicServiceDetail[] {
  return [...services].sort((a, b) => {
    if (!!a.promotionId !== !!b.promotionId) return a.promotionId ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

export function sortPromotedServicesFirst(
  services: PublicServiceDetail[],
): PublicServiceDetail[] {
  const promotedServices: PublicServiceDetail[] = [];
  const otherServices: PublicServiceDetail[] = [];

  for (const service of services) {
    if (service.promotionId) {
      promotedServices.push(service);
    } else {
      otherServices.push(service);
    }
  }

  return [...promotedServices, ...otherServices];
}

function normalizeServiceSearchText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Rank eligible matches without changing the configured order within a tier. */
export function sortServicesForSearch(
  services: PublicServiceDetail[],
  searchQuery: string,
): PublicServiceDetail[] {
  const query = normalizeServiceSearchText(searchQuery);
  // Promotion order is also the stable tie-breaker during search.
  const orderedServices = sortPromotedServicesFirst(services);
  if (!query) return orderedServices;

  const terms = query.split(" ");
  return orderedServices
    .map((service) => {
      const name = normalizeServiceSearchText(service.name);
      const description = normalizeServiceSearchText(service.description ?? "");
      const nameTerms = terms.filter((term) => name.includes(term)).length;
      const descriptionTerms = terms.filter((term) =>
        description.includes(term),
      ).length;
      const rank =
        name === query
          ? 6
          : name.startsWith(query)
            ? 5
            : name.includes(query)
              ? 4
              : nameTerms === terms.length
                ? 3
                : nameTerms > 0
                  ? 2
                  : descriptionTerms > 0
                    ? 1
                    : 0;
      return { service, rank, nameTerms, descriptionTerms };
    })
    .sort(
      (a, b) =>
        b.rank - a.rank ||
        b.nameTerms - a.nameTerms ||
        b.descriptionTerms - a.descriptionTerms,
    )
    .map(({ service }) => service);
}

export function sortCategoriesAlpha(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const aOther = a.trim().toLowerCase() === "other";
    const bOther = b.trim().toLowerCase() === "other";
    if (aOther && !bOther) return 1;
    if (!aOther && bOther) return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
}

// Sort by configured category display order while retaining the established
// popularity / alpha behavior for ties. Many existing tenants still have the
// same displayOrder on multiple categories, so changing this tiebreaker would
// unexpectedly reorder their patient-facing catalogs.
export function sortCategoryGroupsWithDisplayOrder(
  groups: SimpleServiceGroup[],
  categories: Category[] | undefined,
): SimpleServiceGroup[] {
  const orderByName = new Map(
    (categories ?? []).map((category) => [
      category.name,
      category.displayOrder ?? Infinity,
    ]),
  );
  return [...groups].sort((a, b) => {
    const aOrder = orderByName.get(a.id) ?? Infinity;
    const bOrder = orderByName.get(b.id) ?? Infinity;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return sortCategoryGroups(a, b);
  });
}

export function shouldSubgroupByCategory(
  backendFlag: boolean | undefined,
  groups: { services?: { category?: string }[] }[] | undefined,
): boolean {
  if (backendFlag) return true;
  if (!groups) return false;
  return groups.some((g) => {
    const services = g.services ?? [];
    const unique = new Set(
      services.map((s) => (s.category ? s.category : "Other Services")),
    );
    return unique.size > 1;
  });
}

export function sortCategoryGroups(
  a: SimpleServiceGroup,
  b: SimpleServiceGroup,
): number {
  // "Other" goes last
  const aOther = (a.title || "").trim().toLowerCase() === "other";
  const bOther = (b.title || "").trim().toLowerCase() === "other";
  if (aOther && !bOther) return 1;
  if (!aOther && bOther) return -1;

  // Sort by popular count (descending)
  const aPopular = a.services.filter((s) => s.isPopular).length;
  const bPopular = b.services.filter((s) => s.isPopular).length;
  if (aPopular !== bPopular) return bPopular - aPopular;

  // Then alphabetically
  return (a.title || "").localeCompare(b.title || "", undefined, {
    sensitivity: "base",
  });
}

export function groupServicesByCategory(
  services: PublicServiceDetail[],
  categories?: Category[],
): SimpleServiceGroup[] {
  const promotionServices = services.filter(isPromotionService);
  const useSyntheticPromotionGroup =
    promotionServices.length > 0 &&
    !categories?.some((category) => isPromotionCategoryName(category.name));
  const bucketMap = new Map<string, PublicServiceDetail[]>();
  for (const service of services) {
    if (useSyntheticPromotionGroup && isPromotionService(service)) continue;
    const key = service.category ?? "Other Services";
    if (!bucketMap.has(key)) bucketMap.set(key, []);
    bucketMap.get(key)!.push(service);
  }

  const categoryGroups: SimpleServiceGroup[] = Array.from(bucketMap.entries())
    .map(([name, services]) => ({
      id: name,
      title: name,
      services: sortPromotedServicesFirst(services),
    }))
    .filter((g) => g.services.length > 0);

  const sortedGroups = categories?.length
    ? sortCategoryGroupsWithDisplayOrder(categoryGroups, categories)
    : categoryGroups.sort(sortCategoryGroups);

  // Preserve the canonical server order while retaining hierarchy metadata for
  // indentation. A parent may intentionally appear after its child in that
  // order, so hierarchy must not perform a second reordering pass.
  const categoryByName = new Map(
    (categories ?? []).map((category) => [category.name, category]),
  );
  const renderedGroupIds = new Set(sortedGroups.map((group) => group.id));
  const groupsWithHierarchy = sortedGroups.map((group) => {
    let depth = 0;
    let parentName = categoryByName.get(group.id)?.parentCategoryName;
    const seen = new Set([group.id]);
    while (parentName && renderedGroupIds.has(parentName)) {
      if (seen.has(parentName)) {
        depth = 0;
        break;
      }
      seen.add(parentName);
      depth += 1;
      parentName = categoryByName.get(parentName)?.parentCategoryName;
    }
    return {
      ...group,
      depth,
      parentCategoryName:
        categoryByName.get(group.id)?.parentCategoryName ?? null,
    };
  });

  if (!useSyntheticPromotionGroup) return groupsWithHierarchy;

  return [
    {
      id: PROMOTION_CATEGORY_NAME,
      title: PROMOTION_CATEGORY_NAME,
      services: sortPromotedServicesFirst(promotionServices),
    },
    ...groupsWithHierarchy,
  ];
}

/** The same provider availability rules used by the service cards. */
export function getUnavailableServiceIds({
  services,
  providers,
  selectedServices,
  selectedServiceIds,
  selectedLocationId,
  selectedProviderId,
}: {
  services: PublicServiceDetail[] | undefined;
  providers: PublicProviderSummary[] | undefined;
  selectedServices: PublicServiceDetail[] | undefined;
  selectedServiceIds: string[];
  selectedLocationId: string | null;
  selectedProviderId: string | null;
}): Set<string> {
  const result = new Set<string>();
  if (!services || services.length === 0) return result;

  // A service with zero assigned providers can never generate availability —
  // the clinic hasn't wired anyone up for it yet. Surface it in the catalog
  // (patients still see the clinic's full offering) but mark it unavailable
  // so the card renders greyed-out and can't be clicked into an empty time
  // picker. Same signal the RWG feed filter uses on its end.
  //
  // Exception: services intentionally marked onlyBookableOverThePhone don't
  // need a self-schedule provider — clicking one opens the "Call now to
  // book" dialog rather than the availability picker. Keep those clickable.
  for (const svc of services) {
    if (svc.onlyBookableOverThePhone) continue;
    const ids = svc.effectiveProviderIds ?? svc.providerIds ?? [];
    if (ids.length === 0) result.add(svc.id);
  }

  if (!providers || providers.length === 0) return result;
  if (!selectedServices || selectedServices.length === 0) return result;

  const locId = selectedLocationId;
  const providerMatchesLocation = (p: (typeof providers)[number]) =>
    isProviderVisibleAtLocation(p.visibleLocationIds, locId);

  // Providers available at the chosen location, restricted to the committed
  // provider when the patient picked a specific one (e.g. a provider link or
  // reschedule flow). A service with no assigned providers contributes an
  // empty set on the backend, collapsing eligibility to none, so we treat it
  // the same here (empty = no one, NOT "anyone").
  const locationProviderIds = providers
    .filter((p) => {
      if (!providerMatchesLocation(p)) return false;
      if (selectedProviderId && p.id !== selectedProviderId) return false;
      return true;
    })
    .map((p) => p.id);

  // Multi-provider self-scheduling allows each service to carry its own
  // provider (performed back-to-back), so a candidate stays available as long
  // as SOME provider at this location can perform it; only truly unbookable
  // services (no location provider) grey out.
  const eligibleSet = new Set(locationProviderIds);

  for (const svc of services) {
    if (svc.onlyBookableOverThePhone || selectedServiceIds.includes(svc.id)) {
      continue;
    }
    const ids = svc.effectiveProviderIds ?? svc.providerIds ?? [];
    const ok = ids.some((id) => eligibleSet.has(id));
    if (!ok) result.add(svc.id);
  }
  return result;
}
